import { useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  PayTransactionQuickRequest,
  PayTransactionResponse,
} from "Infrastructure/Api/Api";
import { postTransactionsQuickPay } from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";
import { trackPayRedirectToBank } from "Infrastructure/Utils/MixpanelUtils";
import { useRef } from "react";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";
import { transactionsRefundsKeys } from "Api/Queries/Transactions/useTransactionsRefunds";
import { isIOS } from "Utils/Platform/PlatformUtils";

export const useTransactionQuickPay = () => {
  const { t } = useResource();
  const queryClient = useQueryClient();
  // When the pay request left the browser — measured against the moment we leave for the bank.
  const payStartedAt = useRef<number | null>(null);

  return useMutation({
    onMutate: () => {
      payStartedAt.current = performance.now();
    },
    mutationFn: processResponseWithApiProblemDetails(
      // shouldReturnToNativeApp is decided here (via isIOS()), not by callers, so it is omitted
      // from the input type.
      (request: Omit<PayTransactionQuickRequest, "shouldReturnToNativeApp">) =>
        postTransactionsQuickPay({
          ...request,
          // Only the iOS Capacitor shell handles the moone:// return scheme. getPlatform() is
          // "ios" only inside that shell (not in mobile Safari), so isIOS() is the precise check.
          shouldReturnToNativeApp: isIOS(),
        }),
    ),
    onSuccess: ({ signingUrl }: PayTransactionResponse) => {
      queryClient.invalidateQueries({ queryKey: transactionsRefundsKeys.all });
      if (signingUrl) {
        trackPayRedirectToBank(signingUrl, payStartedAt.current);
        window.location.href = signingUrl;
      } else {
        toast.error(t(Resources.TransactionErrors.UnknownPaymentError));
      }
    },
    onError: undefined, // this disables the global handler from App.tsx (so we don't show toast 2x since we have custom error handling)
  });
};
